const API_BASE = import.meta.env.VITE_API_BASE || '';

class ApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request(path, options = {}) {
  const { headers, ...rest } = options;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...rest,
    headers: headers || undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !String(path).includes('/api/auth/me')) {
      window.dispatchEvent(new Event('rollscan:session-expired'));
    }
    throw new ApiError(data.message || `Request failed (${res.status})`, res.status);
  }
  return data;
}

export async function fetchAuthMe() {
  return request('/api/auth/me');
}

export async function fetchRoles() {
  const res = await request('/api/roles');
  return res.data;
}

export async function fetchMembers() {
  const res = await request('/api/members');
  return res.data;
}

export async function fetchMemberCandidates() {
  const res = await request('/api/members/candidates');
  return res.data;
}

export async function upsertMember(payload) {
  const res = await request('/api/members', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function updateMemberRoles(userId, roles) {
  const res = await request(`/api/members/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roles }),
  });
  return res.data;
}

export async function deactivateMember(userId) {
  await request(`/api/members/${userId}`, { method: 'DELETE' });
}

export function inspectionImageUrl(row) {
  const file = row?.imageFile?.filename;
  if (!file) return '';
  const v = row?.updatedAt || row?.createdAt || row?.id || Date.now();
  return `${API_BASE}/uploads/${encodeURIComponent(file)}?v=${encodeURIComponent(String(v))}`;
}

export function inspectionSourceImageUrl(row) {
  const file = row?.sourceImageFile?.filename;
  if (!file) return '';
  const v = row?.updatedAt || row?.createdAt || row?.id || Date.now();
  return `${API_BASE}/uploads/${encodeURIComponent(file)}?v=${encodeURIComponent(String(v))}`;
}

export async function fetchRules() {
  const res = await request('/api/inspections/rules');
  return res.data;
}

export async function fetchInspections(limit = 50, filters = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (filters.customerId) params.set('customerId', filters.customerId);
  const res = await request(`/api/inspections?${params}`);
  return res.data;
}

export async function fetchCustomers() {
  const res = await request('/api/customers');
  return res.data;
}

export async function fetchCustomer(id) {
  const res = await request(`/api/customers/${id}`);
  return res.data;
}

export async function createCustomer(payload) {
  const res = await request('/api/customers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function updateCustomer(id, payload) {
  const res = await request(`/api/customers/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function deleteCustomer(id) {
  await request(`/api/customers/${id}`, { method: 'DELETE' });
}

export async function fetchPaperGrades(activeOnly = false) {
  const params = new URLSearchParams();
  if (activeOnly) params.set('activeOnly', '1');
  const qs = params.toString();
  const res = await request(`/api/paper-grades${qs ? `?${qs}` : ''}`);
  return res.data;
}

export async function fetchPaperGrade(id) {
  const res = await request(`/api/paper-grades/${id}`);
  return res.data;
}

export async function createPaperGrade(payload) {
  const res = await request('/api/paper-grades', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function updatePaperGrade(id, payload) {
  const res = await request(`/api/paper-grades/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function deletePaperGrade(id) {
  await request(`/api/paper-grades/${id}`, { method: 'DELETE' });
}

export async function fetchPaperWidths(activeOnly = false) {
  const params = new URLSearchParams();
  if (activeOnly) params.set('activeOnly', '1');
  const qs = params.toString();
  const res = await request(`/api/paper-widths${qs ? `?${qs}` : ''}`);
  return res.data;
}

export async function createPaperWidth(payload) {
  const res = await request('/api/paper-widths', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function updatePaperWidth(id, payload) {
  const res = await request(`/api/paper-widths/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.data;
}

export async function deletePaperWidth(id) {
  await request(`/api/paper-widths/${id}`, { method: 'DELETE' });
}

export async function fetchProblemSpecs() {
  const res = await request('/api/problem-specs');
  return res.data;
}

export async function fetchProblemSpec(id) {
  const res = await request(`/api/problem-specs/${id}`);
  return res.data;
}

export async function createProblemSpec(payload, files = []) {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  files.forEach((file) => form.append('images', file));
  const res = await request('/api/problem-specs', {
    method: 'POST',
    body: form,
  });
  return res.data;
}

export async function updateProblemSpec(id, payload, files = []) {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  files.forEach((file) => form.append('images', file));
  const res = await request(`/api/problem-specs/${id}`, {
    method: 'PATCH',
    body: form,
  });
  return res.data;
}

export async function deleteProblemSpec(id) {
  await request(`/api/problem-specs/${id}`, { method: 'DELETE' });
}

export function uploadUrl(filename) {
  if (!filename) return '';
  return `${API_BASE}/uploads/${encodeURIComponent(filename)}?v=1`;
}

export async function fetchInspection(id) {
  const res = await request(`/api/inspections/${id}`);
  return res.data;
}

export async function submitInspection({ result, blob, sourceBlob, fileName, sourceFileName }) {
  const form = new FormData();
  form.append('result', JSON.stringify(result));
  if (blob) {
    form.append('image', blob, fileName || `scan-${Date.now()}.jpg`);
  }
  if (sourceBlob) {
    form.append(
      'sourceImage',
      sourceBlob,
      sourceFileName || `scan-source-${Date.now()}.jpg`
    );
  }
  const res = await request('/api/inspections', {
    method: 'POST',
    body: form,
  });
  return res.data;
}

export async function updateInspection(id, fields) {
  const res = await request(`/api/inspections/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  return res.data;
}

export async function updateInspectionWithImage(id, fields, blob, fileName) {
  const form = new FormData();
  form.append('payload', JSON.stringify(fields));
  if (blob) {
    form.append('image', blob, fileName || `rollscan-updated-${Date.now()}.jpg`);
  }
  const res = await request(`/api/inspections/${id}`, {
    method: 'PATCH',
    body: form,
  });
  return res.data;
}
